// =============================================================================
// Token Debugger - Token调试组件
// =============================================================================

import React, { useState, useEffect } from 'react';
import { Key, CheckCircle, XCircle, RefreshCw, AlertTriangle } from 'lucide-react';
import { getDebugInfo, getToken, apiCall } from '../utils/api';

export default function TokenDebugger() {
  const [debugInfo, setDebugInfo] = useState(null);
  const [tokenValid, setTokenValid] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    updateDebugInfo();
  }, []);

  const updateDebugInfo = () => {
    const info = getDebugInfo();
    setDebugInfo(info);
  };

  const validateToken = async () => {
    setLoading(true);
    try {
      // 尝试调用一个需要认证的API
      const response = await apiCall('/api/terminals/stats/resources');
      const data = await response.json();
      
      if (data.success) {
        setTokenValid(true);
      } else {
        setTokenValid(false);
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      setTokenValid(false);
    } finally {
      setLoading(false);
    }
  };

  const clearAllTokens = () => {
    // 清除所有可能的token键名
    const possibleKeys = ['auth_token', 'auth-token', 'token', 'authToken'];
    possibleKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    updateDebugInfo();
    setTokenValid(null);
  };

  const redirectToLogin = () => {
    window.location.href = '/login';
  };

  if (!debugInfo) {
    return <div>Loading debug info...</div>;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
      <div className="flex items-center space-x-2">
        <Key className="w-5 h-5 text-blue-500" />
        <h3 className="font-medium text-gray-800">Token调试信息</h3>
      </div>

      <div className="space-y-3">
        {/* 基本信息 */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Token存在:</span>
            <span className={`ml-2 font-medium ${debugInfo.hasToken ? 'text-green-600' : 'text-red-600'}`}>
              {debugInfo.hasToken ? '是' : '否'}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Token键名:</span>
            <span className="ml-2 font-mono text-xs bg-gray-100 px-1 rounded">
              {debugInfo.tokenKey}
            </span>
          </div>
          <div>
            <span className="text-gray-600">当前URL:</span>
            <span className="ml-2 text-xs text-blue-600 truncate">
              {debugInfo.currentUrl}
            </span>
          </div>
          <div>
            <span className="text-gray-600">API Base:</span>
            <span className="ml-2 text-xs text-blue-600">
              {debugInfo.apiBase}
            </span>
          </div>
        </div>

        {/* Token详情 */}
        {debugInfo.hasToken && (
          <div className="border-t pt-3">
            <div className="text-sm text-gray-600 mb-2">Token详情:</div>
            <div className="bg-gray-50 p-2 rounded text-xs font-mono break-all">
              {getToken()}
            </div>
          </div>
        )}

        {/* Token验证 */}
        <div className="border-t pt-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Token有效性:</span>
            <div className="flex items-center space-x-2">
              {tokenValid === null && (
                <span className="text-gray-500 text-sm">未检查</span>
              )}
              {tokenValid === true && (
                <div className="flex items-center space-x-1 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">有效</span>
                </div>
              )}
              {tokenValid === false && (
                <div className="flex items-center space-x-1 text-red-600">
                  <XCircle className="w-4 h-4" />
                  <span className="text-sm">无效</span>
                </div>
              )}
              <button
                onClick={validateToken}
                disabled={loading || !debugInfo.hasToken}
                className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
              >
                {loading ? (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                ) : (
                  '验证'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* 所有localStorage中的token相关键 */}
        <div className="border-t pt-3">
          <div className="text-sm text-gray-600 mb-2">localStorage中的token相关键:</div>
          <div className="space-y-1">
            {['auth_token', 'auth-token', 'token', 'authToken'].map(key => {
              const value = localStorage.getItem(key);
              return (
                <div key={key} className="flex items-center justify-between text-xs">
                  <span className="font-mono bg-gray-100 px-1 rounded">{key}:</span>
                  <span className={value ? 'text-green-600' : 'text-gray-400'}>
                    {value ? '存在' : '不存在'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="border-t pt-3 flex space-x-2">
          <button
            onClick={updateDebugInfo}
            className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            刷新信息
          </button>
          
          <button
            onClick={clearAllTokens}
            className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
          >
            清除所有Token
          </button>
          
          {!debugInfo.hasToken && (
            <button
              onClick={redirectToLogin}
              className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
            >
              前往登录
            </button>
          )}
        </div>

        {/* 警告信息 */}
        {!debugInfo.hasToken && (
          <div className="border-t pt-3">
            <div className="flex items-start space-x-2 text-sm text-amber-600 bg-amber-50 p-2 rounded">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium">未找到认证Token</div>
                <div className="text-xs mt-1">
                  请先登录以获取有效的认证token。如果已经登录但仍然看到此消息，可能是token已过期。
                </div>
              </div>
            </div>
          </div>
        )}

        {tokenValid === false && debugInfo.hasToken && (
          <div className="border-t pt-3">
            <div className="flex items-start space-x-2 text-sm text-red-600 bg-red-50 p-2 rounded">
              <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium">Token无效或已过期</div>
                <div className="text-xs mt-1">
                  当前token无法通过服务器验证，请重新登录获取新的token。
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
